<script setup lang="ts">
import { computed } from 'vue';
import {
  X,
  Phone,
  Mail,
  MapPin,
  Car,
  Banknote,
  Calendar,
  MessageSquare,
  ScanLine,
  AlertCircle,
  Award,
} from 'lucide-vue-next';
import Avatar from './Avatar.vue';
import DataSourceBadge, { type DataSource } from '@/components/ui/DataSourceBadge.vue';

/**
 * Customer 360 — slide-over z pelnym profilem klienta.
 * Agreguje dane z roznych zrodel: sales, leads, document scans, communication.
 *
 * Otwierany przez klikniecie nazwiska klienta w activity / sales / pipeline.
 */

export type CustomerEvent = {
  id: string;
  kind: 'sale' | 'lead' | 'call' | 'email' | 'sms' | 'meeting' | 'document' | 'test_drive';
  title: string;
  meta?: string;
  at: string;
  amount?: number;
  brand?: string;
  source?: DataSource;
};

export type CustomerProfile = {
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  /** Klasa klienta: nowy / regular / vip */
  tier?: 'new' | 'regular' | 'vip';
  /** Lifetime value w groszach */
  ltvCents?: number;
  /** Lista zdarzen — sortowana DESC */
  timeline: CustomerEvent[];
  /** Otwarte leady (count) */
  openLeads?: number;
  /** Zamkniete sprzedaze (count) */
  closedSales?: number;
};

const props = defineProps<{
  open: boolean;
  profile: CustomerProfile | null;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

function close() {
  emit('update:open', false);
}

const fmt = (cents: number) =>
  new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 0,
  }).format(cents / 100);

const tierMeta = {
  new: { label: 'Nowy', color: 'var(--color-info)' },
  regular: { label: 'Stale', color: 'var(--color-product-light)' },
  vip: { label: 'VIP', color: 'var(--color-accent)' },
} as const;

const eventMeta = {
  sale: { icon: Car, color: 'var(--color-success)', label: 'Sprzedaz' },
  lead: { icon: AlertCircle, color: 'var(--color-product-light)', label: 'Lead' },
  call: { icon: Phone, color: 'var(--color-product-light)', label: 'Telefon' },
  email: { icon: Mail, color: 'var(--color-info)', label: 'Email' },
  sms: { icon: MessageSquare, color: 'var(--color-info)', label: 'SMS' },
  meeting: { icon: Calendar, color: 'var(--color-warning)', label: 'Spotkanie' },
  document: { icon: ScanLine, color: '#EC4899', label: 'Dokument' },
  test_drive: { icon: Car, color: '#8B5CF6', label: 'Jazda probna' },
} as const;

const stats = computed(() => {
  if (!props.profile) return null;
  const tl = props.profile.timeline;
  return {
    sales: props.profile.closedSales ?? tl.filter((e) => e.kind === 'sale').length,
    leads: props.profile.openLeads ?? tl.filter((e) => e.kind === 'lead').length,
    interactions: tl.filter((e) => ['call', 'email', 'sms', 'meeting'].includes(e.kind)).length,
  };
});
</script>

<template>
  <Teleport to="body">
    <Transition name="cust-fade">
      <div
        v-if="open && profile"
        class="fixed inset-0 z-[150]"
        :style="{ background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' }"
        @click.self="close"
      >
        <Transition name="cust-slide">
          <div
            v-if="open"
            class="fixed top-0 right-0 bottom-0 w-full sm:w-[560px] overflow-y-auto"
            :style="{
              background: 'var(--card-bg)',
              borderLeft: '1px solid var(--border-strong)',
              boxShadow: '-12px 0 32px rgba(0, 0, 0, 0.25)',
            }"
            @click.stop
          >
            <header
              class="sticky top-0 z-10 backdrop-blur-xl px-5 py-4 flex items-center justify-between gap-3"
              :style="{
                background: 'var(--surface-bg-overlay)',
                borderBottom: '1px solid var(--border-soft)',
              }"
            >
              <div class="flex items-center gap-3">
                <Avatar :name="profile.name" :size="44" />
                <div>
                  <p class="text-caps mb-0.5">Customer 360</p>
                  <h2 class="text-lg font-bold">{{ profile.name }}</h2>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-icon btn-ghost"
                aria-label="Zamknij"
                @click="close"
              >
                <X :size="16" />
              </button>
            </header>

            <div class="px-5 py-5 space-y-5">
              <!-- Tier + LTV strip -->
              <section class="flex items-center gap-3 flex-wrap">
                <span
                  v-if="profile.tier"
                  class="badge font-bold inline-flex items-center gap-1"
                  :style="{
                    background: `color-mix(in srgb, ${tierMeta[profile.tier].color} 16%, transparent)`,
                    color: tierMeta[profile.tier].color,
                  }"
                >
                  <Award v-if="profile.tier === 'vip'" :size="12" />
                  {{ tierMeta[profile.tier].label }}
                </span>
                <div v-if="profile.ltvCents" class="ml-auto text-right">
                  <p class="text-caps mb-0.5">Lifetime value</p>
                  <p class="numeric font-bold">{{ fmt(profile.ltvCents) }}</p>
                </div>
              </section>

              <!-- Contact info -->
              <section class="card-glass p-4 space-y-2">
                <div v-if="profile.phone" class="flex items-center gap-2 text-sm">
                  <Phone :size="14" :style="{ color: 'var(--color-product-light)' }" />
                  <a :href="`tel:${profile.phone}`" class="numeric font-semibold hover:underline">
                    {{ profile.phone }}
                  </a>
                </div>
                <div v-if="profile.email" class="flex items-center gap-2 text-sm">
                  <Mail :size="14" :style="{ color: 'var(--color-info)' }" />
                  <a :href="`mailto:${profile.email}`" class="hover:underline">
                    {{ profile.email }}
                  </a>
                </div>
                <div v-if="profile.address" class="flex items-start gap-2 text-sm">
                  <MapPin :size="14" :style="{ color: 'var(--surface-fg-muted)' }" class="mt-0.5" />
                  <span :style="{ color: 'var(--surface-fg-2)' }">{{ profile.address }}</span>
                </div>
              </section>

              <!-- Stats trio -->
              <section v-if="stats" class="grid grid-cols-3 gap-2">
                <div class="card p-3 text-center">
                  <p class="numeric text-2xl font-bold" :style="{ color: 'var(--color-success)' }">
                    {{ stats.sales }}
                  </p>
                  <p
                    class="text-[10px] uppercase tracking-wider"
                    :style="{ color: 'var(--surface-fg-faint)' }"
                  >
                    sprzedaze
                  </p>
                </div>
                <div class="card p-3 text-center">
                  <p
                    class="numeric text-2xl font-bold"
                    :style="{ color: 'var(--color-product-light)' }"
                  >
                    {{ stats.leads }}
                  </p>
                  <p
                    class="text-[10px] uppercase tracking-wider"
                    :style="{ color: 'var(--surface-fg-faint)' }"
                  >
                    leady
                  </p>
                </div>
                <div class="card p-3 text-center">
                  <p class="numeric text-2xl font-bold" :style="{ color: 'var(--surface-fg)' }">
                    {{ stats.interactions }}
                  </p>
                  <p
                    class="text-[10px] uppercase tracking-wider"
                    :style="{ color: 'var(--surface-fg-faint)' }"
                  >
                    interakcje
                  </p>
                </div>
              </section>

              <!-- Timeline -->
              <section class="space-y-3">
                <header class="flex items-center justify-between gap-2">
                  <h3 class="text-caps">Pelna historia</h3>
                  <span class="badge badge-info">{{ profile.timeline.length }}</span>
                </header>
                <ul class="space-y-2">
                  <li
                    v-for="ev in profile.timeline"
                    :key="ev.id"
                    class="card p-3 flex items-start gap-3"
                  >
                    <div
                      class="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      :style="{
                        background: `color-mix(in srgb, ${eventMeta[ev.kind].color} 14%, transparent)`,
                        color: eventMeta[ev.kind].color,
                      }"
                    >
                      <component :is="eventMeta[ev.kind].icon" :size="15" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-baseline gap-2 mb-0.5 flex-wrap">
                        <span class="font-semibold text-sm">{{ ev.title }}</span>
                        <span v-if="ev.brand" class="badge badge-product" style="font-size: 10px">
                          {{ ev.brand }}
                        </span>
                        <DataSourceBadge v-if="ev.source" :source="ev.source" size="xs" />
                      </div>
                      <p v-if="ev.meta" class="text-xs" :style="{ color: 'var(--surface-fg-2)' }">
                        {{ ev.meta }}
                      </p>
                      <div
                        class="text-xs mt-1 flex items-center gap-2"
                        :style="{ color: 'var(--surface-fg-faint)' }"
                      >
                        <Calendar :size="10" />
                        <span>{{ ev.at }}</span>
                        <span
                          v-if="ev.amount"
                          class="ml-auto numeric font-semibold"
                          :style="{ color: 'var(--surface-fg)' }"
                        >
                          <Banknote :size="10" class="inline -mt-0.5" />
                          {{ fmt(ev.amount) }}
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cust-fade-enter-active,
.cust-fade-leave-active {
  transition: opacity 0.25s var(--ease-smooth);
}
.cust-fade-enter-from,
.cust-fade-leave-to {
  opacity: 0;
}

.cust-slide-enter-active,
.cust-slide-leave-active {
  transition: transform 0.3s var(--ease-spring);
}
.cust-slide-enter-from,
.cust-slide-leave-to {
  transform: translateX(100%);
}
</style>
